import { cn } from '@workspace/ui/lib/utils'
import { CircleAlert, CircleCheck } from 'lucide-react'
import type { ReactNode } from 'react'

export interface CalloutProps {
  children: ReactNode
  /**
   * `bad` — states plainly that something is wrong, typically on our side.
   * `warn` — something needs doing before the product can carry on. Nothing is
   *   broken, so it must not borrow `bad`'s red.
   * `good` — confirms something has been verified.
   */
  tone: 'bad' | 'warn' | 'good'
  /** Replaces the default tone icon. */
  icon?: ReactNode
  className?: string
}

/**
 * A single tinted sentence at the top of a card, stating the one thing that
 * matters before any supporting detail.
 *
 * Used where the honest summary needs to lead — "everything on your side is
 * fine, sending stopped on ours" — rather than being buried in a list.
 */
export function Callout({ children, tone, icon, className }: CalloutProps) {
  const Icon = tone === 'good' ? CircleCheck : CircleAlert
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 rounded-[10px] border px-3.5 py-3 text-[13px] text-foreground',
        tone === 'bad'
          ? 'border-red-400/30 bg-red-400/10'
          : tone === 'warn'
            ? 'border-amber-400/30 bg-amber-400/10'
            : 'border-emerald-400/30 bg-emerald-400/10',
        className
      )}
    >
      {icon ?? (
        <Icon
          className={cn(
            'size-[17px] shrink-0',
            tone === 'bad'
              ? 'text-red-400'
              : tone === 'warn'
                ? 'text-amber-400'
                : 'text-emerald-400'
          )}
          aria-hidden
        />
      )}
      <span>{children}</span>
    </div>
  )
}
